import { customListSchemaApi, DEFAULT_RETRY_COUNT } from '../../apis';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type InternalApiCustomListSchemaField, InternalApiEntityOrder } from '../../api-internal';

export const useQueryCustomListSchemaFields = (customListId: string) => {
  return useQuery({
    queryKey: ['customListSchema', customListId],
    queryFn: () =>
      customListSchemaApi.getCustomListSchema({ customListId }).then((response) => {
        return response.schemaFields.sort((a, b) => a.sortOrder - b.sortOrder);
      }),
    retry: (failureCount, error) => error.status !== 404 && failureCount < DEFAULT_RETRY_COUNT,
  });
};

export const useUpdateCustomListEntitySchemaFields = (customListId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (schemaFields: InternalApiCustomListSchemaField[]) =>
      customListSchemaApi
        .updateCustomListSchema({
          customListId: customListId,
          internalApiCustomListSchema: {
            customListId: customListId,
            schemaFields: schemaFields,
          },
        })
        .then((result) => result.schemaFields),
    onSuccess: (schemaFields) => {
      queryClient.setQueryData(['customListSchema', customListId], schemaFields);
      queryClient.invalidateQueries({ queryKey: ['customListSignificantFields'] });
    },
  });
};

export const useSortCustomListSchemaFields = (customListId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fieldOrder: InternalApiEntityOrder[]) =>
      customListSchemaApi
        .sortCustomListSchema({ customListId: customListId, internalApiEntityOrder: fieldOrder })
        .then((result) => result.schemaFields),
    onSuccess: (schemaFields) => {
      queryClient.setQueryData(['customListSchema', customListId], schemaFields);
    },
  });
};
